import { useState, Suspense, lazy, useRef } from 'react';
import { RotatingHeadline } from '../components/RotatingHeadline';
import { GlowingStar } from '../components/GlowingStar';
import { SEO } from '../components/SEO';
import { Header, Footer } from '../components/layout';
import { Link } from 'react-router-dom';
import { Container, Badge } from '../components/ui';
import { useVisibilitySentinel } from '../hooks/useVisibilitySentinel';
import { usePerformanceSentinel } from '../hooks/usePerformanceSentinel';

const Starfield = lazy(() => import('../components/Starfield').then(m => ({ default: m.Starfield })));
const RegistrySearch = lazy(() => import('../components/RegistrySearch').then(m => ({ default: m.RegistrySearch })));
const BuilderDemo = lazy(() => import('../components/BuilderDemo').then(m => ({ default: m.BuilderDemo })));
const UseDemoSection = lazy(() => import('../components/UseDemoSection').then(m => ({ default: m.UseDemoSection })));
const AutomateDemoSection = lazy(() => import('../components/AutomateDemoSection').then(m => ({ default: m.AutomateDemoSection })));
const NavigateTabContent = lazy(() => import('../components/NavigateTabContent').then(m => ({ default: m.NavigateTabContent })));
const EarnTabContent = lazy(() => import('../components/EarnTabContent').then(m => ({ default: m.EarnTabContent })));
const IntegrateTabContent = lazy(() => import('../components/IntegrateTabContent').then(m => ({ default: m.IntegrateTabContent })));
const ConvertTabContent = lazy(() => import('../components/ConvertTabContent').then(m => ({ default: m.ConvertTabContent })));
const ComposeTabContent = lazy(() => import('../components/ComposeTabContent').then(m => ({ default: m.ComposeTabContent })));
const PlanetShowcase = lazy(() => import('../components/PlanetShowcase').then(m => ({ default: m.PlanetShowcase })));
const MCPClientsSlider = lazy(() => import('../components/MCPClientsSlider').then(m => ({ default: m.MCPClientsSlider })));
const StarshipAnimation = lazy(() => import('../components/StarshipAnimation').then(m => ({ default: m.StarshipAnimation })));
const ISSVisual = lazy(() => import('../components/ISSVisual').then(m => ({ default: m.ISSVisual })));
const SolarSystem = lazy(() => import('../components/SolarSystem').then(m => ({ default: m.SolarSystem })));
const Tool3DSlider = lazy(() => import('../components/Tool3DSlider').then(m => ({ default: m.Tool3DSlider })));
const StarSystemVisual = lazy(() => import('../components/StarSystemVisual').then(m => ({ default: m.StarSystemVisual })));

/**
 * DeferredSection - Only renders children when it becomes visible.
 */
function DeferredSection({ children, minHeight = '300px' }: { children: React.ReactNode; minHeight?: string }) {
    const ref = useRef<HTMLDivElement>(null);
    const { isVisible } = useVisibilitySentinel(ref as any);
    const [shouldRender, setShouldRender] = useState(false);

    if (isVisible && !shouldRender) {
        setShouldRender(true);
    }

    return (
        <div ref={ref} style={{ minHeight: shouldRender ? 'auto' : minHeight }}>
            {shouldRender ? children : null}
        </div>
    );
}

// Loading spinner component for Suspense fallback
function TabLoader() {
    return (
        <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center gap-4">
                {/* Spinner matching the style in RegistrySearch input (border-b-2 border-accent) */}
                <div className="rounded-full h-8 w-8 border-b-2 border-accent animate-spin" />
                <span className="text-sm text-muted">Loading...</span>
            </div>
        </div>
    );
}

/**
 * HomePage - Landing page for MCP Cloud with Tabbed interface
 */
export function HomePage() {
    const [activeTab, setActiveTab] = useState<'create' | 'use' | 'automate' | 'find' | 'compose' | 'convert' | 'integrate' | 'earn'>('create');
    const { isPanic } = usePerformanceSentinel();
    const infinityRef = useRef<HTMLDivElement>(null);
    const { isVisible: isInfinityVisible } = useVisibilitySentinel(infinityRef as any);
    const isActive = !isPanic && isInfinityVisible;

    return (
        <main id="main-content" className="min-h-screen">
            <SEO
                title="MCP Cloud — Connect AI to everything. Let it do anything."
                description="Deploy, secure, and scale Model Context Protocol (MCP) servers. The premier infrastructure platform to connect your AI assistants to all your data."
                url="https://mcp-cloud.io"
            />
            <Suspense fallback={null}>
                <Starfield />
            </Suspense>
            <Header />

            <Container as="section" className="min-h-[10vh] flex flex-col items-center justify-center pt-0 pb-0 text-center relative">
                <div className="relative w-full flex flex-col items-center pt-8 pb-12">
                    {/* Decorative Background Star Glow */}
                    <GlowingStar className="absolute inset-0" />

                    <Badge className="my-8 relative">Powering AI transformation</Badge>

                    <h1 className="relative mt-0 text-[clamp(40px,6vw,72px)] leading-[1.05] font-bold text-transparent bg-clip-text bg-gradient-to-b from-white to-white/60">
                        MCP <span className="text-accent">Cloud</span>
                    </h1>

                    <h4 className="relative mt-4 text-[clamp(20px,3vw,32px)] text-slate-300 font-medium">
                        Connect AI to <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">everything</span>. Let it do <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">anything</span>.
                    </h4>
                    <RotatingHeadline
                        className="relative mt-4 text-[clamp(14px,1.8vw,20px)] text-muted max-w-4xl min-h-[60px] flex items-center justify-center"
                        texts={[
                            'Deploy your own MCP server authentication, logs and analytics',
                            'Connect ChatGPT to thousands of apps and services',
                            'Break down data silos by connecting AI to all your data',
                            'Deploy AI agents with custom capabilities',
                            'Enforce MCP security policies company-wide',
                            'Manage all your MCP servers in one place',
                        ]}
                        intervalMs={3200}
                    />
                </div>

                {/* Tab Selector */}
                <div className="mt-[-19px] mb-8 w-full max-w-full overflow-x-auto flex justify-start lg:justify-center [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                    <div
                        role="tablist"
                        aria-orientation="horizontal"
                        className="inline-flex h-11 items-center justify-center rounded-lg bg-white/5 p-1 text-muted border border-white/10 min-w-max mx-4 lg:mx-0"
                        style={{ outline: 'none' }}
                    >
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'create'}
                            onClick={() => setActiveTab('create')}
                            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-8 py-2 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:pointer-events-none disabled:opacity-50 ${activeTab === 'create'
                                ? 'bg-white/15 text-white shadow-sm'
                                : 'hover:bg-white/5 hover:text-white'
                                }`}
                        >
                            Create
                        </button>

                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'use'}
                            onClick={() => setActiveTab('use')}
                            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-8 py-2 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:pointer-events-none disabled:opacity-50 ${activeTab === 'use'
                                ? 'bg-white/15 text-white shadow-sm'
                                : 'hover:bg-white/5 hover:text-white'
                                }`}
                        >
                            Use
                        </button>

                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'automate'}
                            onClick={() => setActiveTab('automate')}
                            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-8 py-2 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:pointer-events-none disabled:opacity-50 ${activeTab === 'automate'
                                ? 'bg-white/15 text-white shadow-sm'
                                : 'hover:bg-white/5 hover:text-white'
                                }`}
                        >
                            Automate
                        </button>

                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'find'}
                            onClick={() => setActiveTab('find')}
                            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-8 py-2 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:pointer-events-none disabled:opacity-50 ${activeTab === 'find'
                                ? 'bg-white/15 text-white shadow-sm'
                                : 'hover:bg-white/5 hover:text-white'
                                }`}
                        >
                            Find
                        </button>

                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'compose'}
                            onClick={() => setActiveTab('compose')}
                            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-8 py-2 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:pointer-events-none disabled:opacity-50 ${activeTab === 'compose'
                                ? 'bg-white/15 text-white shadow-sm'
                                : 'hover:bg-white/5 hover:text-white'
                                }`}
                        >
                            Compose
                        </button>

                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'convert'}
                            onClick={() => setActiveTab('convert')}
                            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-8 py-2 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:pointer-events-none disabled:opacity-50 ${activeTab === 'convert'
                                ? 'bg-white/15 text-white shadow-sm'
                                : 'hover:bg-white/5 hover:text-white'
                                }`}
                        >
                            Convert
                        </button>

                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'integrate'}
                            onClick={() => setActiveTab('integrate')}
                            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-8 py-2 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:pointer-events-none disabled:opacity-50 ${activeTab === 'integrate'
                                ? 'bg-white/15 text-white shadow-sm'
                                : 'hover:bg-white/5 hover:text-white'
                                }`}
                        >
                            Integrate
                        </button>

                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'earn'}
                            onClick={() => setActiveTab('earn')}
                            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-8 py-2 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:pointer-events-none disabled:opacity-50 ${activeTab === 'earn'
                                ? 'bg-white/15 text-white shadow-sm'
                                : 'hover:bg-white/5 hover:text-white'
                                }`}
                        >
                            Earn
                        </button>
                    </div>
                </div>

                <Suspense fallback={<TabLoader />}>
                    {activeTab === 'create' && <BuilderDemo />}
                    {activeTab === 'find' && <RegistrySearch />}
                    {activeTab === 'compose' && <ComposeTabContent />}
                    {activeTab === 'convert' && <ConvertTabContent />}
                    {activeTab === 'use' && <UseDemoSection />}
                    {activeTab === 'automate' && <AutomateDemoSection />}
                    {activeTab === 'integrate' && <IntegrateTabContent />}
                    {activeTab === 'earn' && <EarnTabContent />}
                </Suspense>
            </Container>

            <Suspense fallback={<Container><TabLoader /></Container>}>
            </Suspense>

            <section className="mt-24">
                <DeferredSection minHeight="600px">
                    <Suspense fallback={<TabLoader />}>
                        <Container>
                            <div className="mt-36">
                                <PlanetShowcase />
                            </div>
                            <div className="mb-12">
                                <MCPClientsSlider />
                            </div>

                            <div ref={infinityRef} className="flex flex-col items-center justify-center text-center px-4 mt-36">
                                <h2 className="text-[clamp(32px,5vw,52px)] leading-[1.05] font-bold text-white mb-6 flex items-center justify-center gap-3">
                                    Reusable <span className="text-accent space-x-3 flex items-center">toolsets</span>
                                </h2>
                                <h4 className="text-[clamp(18px,2.5vw,28px)] text-slate-300 font-medium">
                                    Combine and compose. <span className="text-accent">Plug into</span> chats, agents, and assistants
                                </h4>
                                <p className="text-white/60 text-lg md:text-xl max-w-3xl mx-auto mt-4">
                                    Switch between ChatGPT and Mistral, Cursor, Claude Code, n8n, OpenClaw, and LibreChat—AI capabilities remain the same
                                </p>
                                <ISSVisual />
                            </div>

                            <div ref={infinityRef} className="flex flex-col items-center justify-center text-center px-4 mt-16">
                                <h2 className="text-[clamp(32px,5vw,52px)] leading-[1.05] font-bold text-white mb-6 flex items-center justify-center gap-3">
                                    Unified <span className="text-accent space-x-3 flex items-center">inventory</span>
                                </h2>
                                <h4 className="text-[clamp(18px,2.5vw,28px)] text-slate-300 font-medium">
                                    One place to manage <span className="text-accent">all your MCPs</span>
                                </h4>
                                <p className="text-white/60 text-lg md:text-xl max-w-3xl mx-auto mt-4 mb-12">
                                    Whether cloud-hosted, self-hosted, or third-party, MCP Cloud provides a single control point with a unified endpoint, authentication, security, and observability
                                </p>
                                <SolarSystem />

                                {/* Inventory page links */}
                                <div className="flex flex-wrap items-center justify-center gap-6 mt-12">
                                    {[
                                        { to: '/mcp-management', label: 'MCP Management' },
                                        { to: '/mcp-hosting', label: 'MCP Hosting' },
                                        { to: '/mcp-web-ui', label: 'MCP Web UI' },
                                    ].map((link) => (
                                        <Link key={link.to} to={link.to} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                                            <span className="text-[11px] font-bold tracking-[0.2em] text-slate-300 group-hover:text-white group-hover:underline underline-offset-4 uppercase whitespace-nowrap transition-colors duration-300">
                                                {link.label}
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                            </div>

                            <div ref={infinityRef} className="flex flex-col items-center justify-center text-center px-4 mt-36">
                                <h2 className="text-[clamp(32px,5vw,52px)] leading-[1.05] font-bold text-white mb-6 flex items-center justify-center gap-3">
                                    Making MCP <span className="text-accent space-x-3 flex items-center">secure</span>
                                </h2>
                                <h4 className="text-[clamp(18px,2.5vw,28px)] text-slate-300 font-medium">
                                    MCP security is a nightmare. <span className="text-accent">We make it a breeze</span>
                                </h4>
                                <p className="text-white/60 text-lg md:text-xl max-w-3xl mx-auto mt-4 mb-8">
                                    Without a proper security layer, MCP can become a black hole for your data. We add that layer — protecting you from prompt injection, tool poisoning, and PII leakage
                                </p>

                                {/* Black hole animation */}
                                <div style={{ position: 'relative', width: 220, height: 220, margin: '2rem auto' }}>
                                    {/* Stars being sucked in */}
                                    {[0, 1, 2, 3, 4].map(i => (
                                        <div key={i} className={`bh-star bh-star--${i}`} />
                                    ))}
                                    {/* Purple glow — slow pulse */}
                                    <div style={{
                                        position: 'absolute',
                                        inset: -30,
                                        borderRadius: '50%',
                                        background: 'radial-gradient(circle, rgba(190,100,255,0.75) 0%, rgba(160,70,255,0.45) 40%, transparent 70%)',
                                        animation: 'bh-glow-pulse 4s ease-in-out infinite',
                                    }} />
                                    {/* Black disk — trembling */}
                                    <div style={{
                                        position: 'absolute',
                                        inset: 0,
                                        borderRadius: '50%',
                                        background: 'radial-gradient(circle, #000 60%, rgba(0,0,0,0.85) 80%, transparent 100%)',
                                        boxShadow: '0 0 40px 15px rgba(0,0,0,0.7), 0 0 80px 30px rgba(80,40,160,0.2)',
                                        animation: 'bh-tremble 0.3s ease-in-out infinite',
                                        zIndex: 2,
                                    }} />
                                </div>

                                <style>{`
                                    .bh-star {
                                        position: absolute;
                                        width: 3px; height: 3px;
                                        background: white;
                                        border-radius: 50%;
                                        box-shadow: 0 0 4px 1px rgba(200,180,255,0.8);
                                        z-index: 1;
                                        opacity: 0;
                                    }
                                    .bh-star--0 { animation: bh-suck-0 4.0s 0.0s ease-in infinite; }
                                    .bh-star--1 { animation: bh-suck-1 4.5s 1.0s ease-in infinite; }
                                    .bh-star--2 { animation: bh-suck-2 3.8s 2.0s ease-in infinite; }
                                    .bh-star--3 { animation: bh-suck-3 4.2s 3.0s ease-in infinite; }
                                    .bh-star--4 { animation: bh-suck-4 4.6s 1.5s ease-in infinite; }

                                    @keyframes bh-suck-0 {
                                        0%   { left: -60%; top: 20%;  opacity: 0; transform: scale(1); }
                                        8%   { opacity: 0.9; }
                                        90%  { opacity: 0.3; transform: scale(0.3); }
                                        100% { left: 48%;  top: 48%;  opacity: 0; transform: scale(0); }
                                    }
                                    @keyframes bh-suck-1 {
                                        0%   { right: -60%; top: 15%; left: auto; opacity: 0; transform: scale(1); }
                                        8%   { opacity: 0.8; }
                                        90%  { opacity: 0.3; transform: scale(0.3); }
                                        100% { right: 46%;  top: 48%; left: auto; opacity: 0; transform: scale(0); }
                                    }
                                    @keyframes bh-suck-2 {
                                        0%   { left: 10%;  top: -60%; opacity: 0; transform: scale(1); }
                                        8%   { opacity: 0.85; }
                                        90%  { opacity: 0.2; transform: scale(0.2); }
                                        100% { left: 48%;  top: 48%;  opacity: 0; transform: scale(0); }
                                    }
                                    @keyframes bh-suck-3 {
                                        0%   { left: 80%;  bottom: -60%; top: auto; opacity: 0; transform: scale(1); }
                                        8%   { opacity: 0.9; }
                                        90%  { opacity: 0.3; transform: scale(0.25); }
                                        100% { left: 48%;  bottom: 46%;  top: auto; opacity: 0; transform: scale(0); }
                                    }
                                    @keyframes bh-suck-4 {
                                        0%   { left: -50%;  top: 75%;  opacity: 0; transform: scale(1); }
                                        8%   { opacity: 0.7; }
                                        90%  { opacity: 0.2; transform: scale(0.3); }
                                        100% { left: 48%;  top: 48%;  opacity: 0; transform: scale(0); }
                                    }

                                    @keyframes bh-glow-pulse {
                                        0%, 100% { transform: scale(1); opacity: 0.7; }
                                        50%      { transform: scale(1.12); opacity: 1; }
                                    }
                                    @keyframes bh-tremble {
                                        0%   { transform: translate(0, 0) scale(1); }
                                        20%  { transform: translate(0.3px, -0.2px) scale(1.015); }
                                        40%  { transform: translate(-0.2px, 0.3px) scale(0.985); }
                                        60%  { transform: translate(0.2px, 0.1px) scale(1.02); }
                                        80%  { transform: translate(-0.2px, -0.2px) scale(0.99); }
                                        100% { transform: translate(0.1px, 0.2px) scale(1.005); }
                                    }
                                `}</style>

                                {/* Security page links */}
                                <div className="flex flex-wrap items-center justify-center gap-6 mt-12">
                                    {[
                                        { to: '/mcp-security', label: 'MCP Security' },
                                        { to: '/mcp-security-scanner', label: 'Security Scanner' },
                                        { to: '/mcp-guardrails', label: 'MCP Guardrails' },
                                        { to: '/security-guards', label: 'Security Guards' },
                                    ].map((link) => (
                                        <Link key={link.to} to={link.to} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                                            <span className="text-[11px] font-bold tracking-[0.2em] text-slate-300 group-hover:text-white group-hover:underline underline-offset-4 uppercase whitespace-nowrap transition-colors duration-300">
                                                {link.label}
                                            </span>
                                        </Link>
                                    ))}
                                </div>

                            </div>

                            <div ref={infinityRef} className="flex flex-col items-center justify-center text-center px-4 mt-36">
                                <h2 className="text-[clamp(32px,5vw,52px)] leading-[1.05] font-bold text-white mb-4 flex items-center justify-center gap-3">
                                    <span className="text-accent space-x-3 flex items-center">
                                        <span className="text-accent">Practical</span>
                                    </span> AI Agents
                                </h2>
                                <h4 className="text-[clamp(18px,2.5vw,28px)] text-slate-300 font-medium">
                                    Launch AI agents <span className="text-accent">supercharged</span> with powerful MCP tools
                                </h4>
                                <p className="text-white/60 text-lg md:text-xl max-w-3xl mx-auto mt-4">
                                    AI agents and AI assistants work 24/7. Deploy them directly in MCP Cloud and extend them with your own custom capabilities
                                </p>
                            </div>
                            <div className="">
                                <StarshipAnimation />
                            </div>
                            <Tool3DSlider />
                            {/* Agents links */}
                            <div className="flex flex-wrap items-center justify-center gap-6 mt-12">
                                {[
                                    { to: '/ai-agent-hosting', label: 'AI Agent Hosting' },
                                    { to: '/mcp-hosting', label: 'MCP Hosting' },
                                ].map((link) => (
                                    <Link key={link.to} to={link.to} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                                        <span className="text-[11px] font-bold tracking-[0.2em] text-slate-300 group-hover:text-white group-hover:underline underline-offset-4 uppercase whitespace-nowrap transition-colors duration-300">
                                            {link.label}
                                        </span>
                                    </Link>
                                ))}
                            </div>

                            <div ref={infinityRef} className="flex flex-col items-center justify-center text-center px-4 mt-36">
                                <h2 className="text-[clamp(32px,5vw,52px)] leading-[1.05] font-bold text-white mb-6 flex items-center justify-center gap-3">
                                    AI that works <span className="text-accent space-x-3 flex items-center">for you</span>
                                </h2>
                                <h4 className="text-[clamp(18px,2.5vw,28px)] text-slate-300 font-medium">
                                    Personalized <span className="text-accent">AI capabilities</span> delivered to your world
                                </h4>
                                <p className="text-white/60 text-lg md:text-xl max-w-3xl mx-auto mt-4">
                                    Dedicated solutions and services for individuals, professionals, developers, and corporate users—your success is our mission
                                </p>
                                <NavigateTabContent />
                            </div>


                            <div ref={infinityRef} className="flex flex-col items-center justify-center text-center px-4 mt-16">
                                <h2 className="text-[clamp(32px,5vw,52px)] leading-[1.05] font-bold text-white mb-6 flex items-center justify-center gap-3">
                                    Universal <span className="text-accent space-x-3 flex items-center">platform</span>
                                    <span className="inline-flex items-center">
                                        <svg
                                            viewBox="0 0 13000 7000"
                                            className="w-[1.7em] h-auto drop-shadow-[0_0_20px_rgba(249,115,22,0.5)] overflow-visible"
                                        >
                                            <defs>
                                                <linearGradient id="moving-infinity-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                                    <stop offset="0%" stopColor="#f97316">
                                                        {isActive && <animate attributeName="offset" values="-1; 1" dur="3s" repeatCount="indefinite" />}
                                                    </stop>
                                                    <stop offset="25%" stopColor="#fdba74">
                                                        {isActive && <animate attributeName="offset" values="-0.75; 1.25" dur="3s" repeatCount="indefinite" />}
                                                    </stop>
                                                    <stop offset="50%" stopColor="#fbbf24">
                                                        {isActive && <animate attributeName="offset" values="-0.5; 1.5" dur="3s" repeatCount="indefinite" />}
                                                    </stop>
                                                    <stop offset="75%" stopColor="#fdba74">
                                                        {isActive && <animate attributeName="offset" values="-0.25; 1.75" dur="3s" repeatCount="indefinite" />}
                                                    </stop>
                                                    <stop offset="100%" stopColor="#f97316">
                                                        {isActive && <animate attributeName="offset" values="0; 2" dur="3s" repeatCount="indefinite" />}
                                                    </stop>
                                                </linearGradient>

                                                {/* Mask to apply gradient along the path strictly */}
                                                <mask id="infinity-mask">
                                                    <path
                                                        d="M9638 6075 c-772 -93 -1504 -467 -2317 -1184 -198 -174 -334 -303 -657 -624 -230 -229 -264 -258 -281 -249 -11 6 -100 93 -199 194 -1144 1171 -1886 1664 -2745 1822 -560 103 -1141 38 -1669 -186 -885 -376 -1533 -1167 -1714 -2093 -41 -207 -51 -318 -51 -565 0 -392 54 -719 176 -1065 338 -964 1145 -1627 2164 -1779 634 -95 1337 26 1957 335 243 121 448 250 703 441 321 239 556 450 1061 950 176 175 327 318 334 318 8 0 77 -66 154 -146 172 -179 592 -584 770 -743 820 -731 1442 -1055 2226 -1163 58 -8 194 -12 350 -12 213 0 279 4 400 23 650 100 1205 379 1659 832 457 457 742 1045 823 1699 19 155 16 563 -6 720 -78 561 -275 1067 -568 1457 -95 128 -294 332 -425 435 -364 290 -837 486 -1378 570 -172 26 -594 34 -767 13z m-6331 -1199 c336 -71 676 -233 1010 -481 119 -88 297 -234 413 -339 146 -133 880 -850 880 -860 0 -16 -640 -649 -795 -788 -492 -438 -947 -715 -1375 -839 -202 -58 -321 -74 -565 -74 -190 0 -235 3 -334 23 -590 119 -1022 504 -1210 1077 -160 489 -130 953 90 1385 252 496 706 824 1266 915 63 10 146 13 293 10 172 -3 225 -8 327 -29z m6863 19 c327 -46 605 -171 841 -378 439 -384 649 -1063 517 -1677 -134 -625 -615 -1131 -1229 -1295 -148 -39 -279 -55 -454 -55 -310 0 -559 62 -870 215 -327 160 -642 391 -1037 761 -248 232 -743 719 -746 734 -3 16 466 495 668 681 682 630 1221 933 1800 1014 130 18 382 18 510 0z"
                                                        fill="white"
                                                    />
                                                </mask>
                                            </defs>

                                            <rect width="13000" height="7000" fill="url(#moving-infinity-gradient)" mask="url(#infinity-mask)" />
                                        </svg>
                                    </span>
                                </h2>
                                <h4 className="text-[clamp(18px,2.5vw,28px)] text-slate-300 font-medium">
                                    Convenient, reliable, simple <span className="text-accent">and secure</span>
                                </h4>
                                <p className="text-white/60 text-lg md:text-xl max-w-3xl mx-auto mt-4">
                                    Create MCP tools, deploy autonomous agents, enforce security policies, and manage everything in one unified platform
                                </p>
                                <div className="w-full relative min-h-[400px] flex items-center justify-center animate-in fade-in duration-700"><StarSystemVisual /></div>
                            </div>

                        </Container>
                    </Suspense>
                </DeferredSection>
            </section>

            <Footer />
        </main>
    );
}
